import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, MapPin, CheckCircle2, Building2, Layers, Sparkles, Route, TrendingUp, ShieldCheck, Eye, Scale } from 'lucide-react';
import ContactForm from '@/components/ContactForm';

const HERO_IMG = 'https://images.unsplash.com/photo-1730415117704-1b31cad3d0c3?auto=format&fit=crop&w=1400&q=80';
const BUILDING_1 = 'https://images.unsplash.com/photo-1664851786731-1b83d2c5bf8e?auto=format&fit=crop&w=1200&q=80';
const BUILDING_2 = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=80';
const BUILDING_3 = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80';
const BUILDING_4 = 'https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&w=1200&q=80';

export default function Home() {
  const { t } = useTranslation();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="section-pad" style={{ paddingTop: 72, paddingBottom: 72 }}>
        <div className="container-zt">
          <div className="hero-grid">
            <div>
              <div className="eyebrow" style={{ marginBottom: 20 }}>{t('hero.eyebrow')}</div>
              <h1 className="font-display hero-title">
                {t('hero.title')} <span className="accent">{t('hero.titleAccent')}</span>
              </h1>
              <p style={{ marginTop: 28, fontSize: 18, lineHeight: 1.6, color: 'var(--ink-2)', maxWidth: 560 }}>{t('hero.subtitle')}</p>
              <div style={{ marginTop: 32, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <a href="#contact" className="btn btn-primary" data-testid="hero-cta-primary">{t('hero.ctaPrimary')} <ArrowRight size={16} /></a>
                <a href="#how" className="btn btn-outline" data-testid="hero-cta-secondary">{t('hero.ctaSecondary')}</a>
              </div>
              <div style={{ marginTop: 32, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <span className="badge-inline"><span className="d" />{t('hero.badge1')}</span>
                <span className="badge-inline"><span className="d" />{t('hero.badge2')}</span>
                <span className="badge-inline"><span className="d" />{t('hero.badge3')}</span>
              </div>
            </div>
            <div className="hero-image">
              <img src={HERO_IMG} alt="Buenos Aires — arquitectura residencial" loading="eager" />
              <span className="hero-tag">Buenos Aires · Puerto Madero</span>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="hairline hairline-b">
        <div className="container-zt">
          <div className="stat-row">
            {[1,2,3,4].map((i) => (
              <div key={i} className="stat-cell">
                <div className="stat-value">{t(`stats.item${i}Value`)}</div>
                <div className="stat-label">{t(`stats.item${i}Label`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="section-pad">
        <div className="container-zt">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 64 }} className="about-grid">
            <div>
              <div className="eyebrow">{t('about.eyebrow')}</div>
              <h2 className="font-display h-section" style={{ marginTop: 12 }}>{t('about.heading')}</h2>
              <div style={{ marginTop: 32, position: 'relative', aspectRatio: '4 / 5', borderRadius: 6, overflow: 'hidden' }}>
                <img src={BUILDING_1} alt="Torre residencial argentina" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontSize: 17, lineHeight: 1.72, color: 'var(--ink-2)' }}>
              <p>{t('about.p1')}</p>
              <p>{t('about.p2')}</p>
              <p>{t('about.p3')}</p>
              <p>{t('about.p4')}</p>
            </div>
          </div>
        </div>
        <style>{`@media (max-width: 900px){ .about-grid { grid-template-columns: 1fr !important; gap: 40px !important; } }`}</style>
      </section>

      {/* PILLARS / CATEGORIES */}
      <section id="projects" className="section-pad" style={{ background: 'var(--paper-2)' }}>
        <div className="container-zt">
          <div style={{ maxWidth: 720 }}>
            <div className="eyebrow">{t('pillars.eyebrow')}</div>
            <h2 className="font-display h-section" style={{ marginTop: 12 }}>{t('pillars.heading')}</h2>
            <p style={{ marginTop: 16, fontSize: 17, color: 'var(--ink-2)', lineHeight: 1.6 }}>{t('pillars.subheading')}</p>
          </div>

          <div style={{ marginTop: 48, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }} className="pillars-grid">
            {[
              { icon: Building2, k: 'i1', img: BUILDING_2 },
              { icon: Layers, k: 'i2', img: BUILDING_3 },
              { icon: Sparkles, k: 'i3', img: BUILDING_4 },
              { icon: Route, k: 'i4', img: BUILDING_1 },
            ].map((it, idx) => {
              const Icon = it.icon;
              return (
                <div key={it.k} className="card-zt" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div className="card-num">{String(idx + 1).padStart(2, '0')}</div>
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <div style={{ aspectRatio: '4/3', borderRadius: 4, overflow: 'hidden' }}>
                    <img src={it.img} alt={t(`pillars.${it.k}Title`)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                  </div>
                  <div>
                    <h3 className="font-display" style={{ fontSize: 22, margin: 0, lineHeight: 1.15 }}>{t(`pillars.${it.k}Title`)}</h3>
                    <p style={{ marginTop: 8, fontSize: 14.5, lineHeight: 1.55, opacity: 0.85 }}>{t(`pillars.${it.k}Text`)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <style>{`@media (max-width: 1100px){ .pillars-grid { grid-template-columns: repeat(2, 1fr) !important; } } @media (max-width: 600px){ .pillars-grid { grid-template-columns: 1fr !important; } }`}</style>
      </section>

      {/* HOW */}
      <section id="how" className="section-pad">
        <div className="container-zt">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 64 }} className="how-grid">
            <div style={{ position: 'sticky', top: 100, alignSelf: 'start' }}>
              <div className="eyebrow">{t('how.eyebrow')}</div>
              <h2 className="font-display h-section" style={{ marginTop: 12 }}>{t('how.heading')}</h2>
              <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 16, color: 'var(--ink-2)', lineHeight: 1.7 }}>
                <p>{t('how.p1')}</p>
                <p>{t('how.p2')}</p>
                <p>{t('how.p3')}</p>
              </div>
              <a href="#contact" className="btn btn-copper" style={{ marginTop: 24 }} data-testid="how-cta">{t('hero.ctaPrimary')} <ArrowRight size={16} /></a>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[1,2,3,4].map((n) => (
                <div key={n} style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 20, padding: '24px 0', borderTop: '1px solid var(--line)' }}>
                  <div className="font-display" style={{ fontSize: 40, lineHeight: 1, color: 'var(--copper)' }}>{String(n).padStart(2, '0')}</div>
                  <div>
                    <h3 className="font-display" style={{ fontSize: 24, margin: 0 }}>{t(`how.step${n}Title`)}</h3>
                    <p style={{ marginTop: 8, color: 'var(--ink-2)', lineHeight: 1.6 }}>{t(`how.step${n}Text`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <style>{`@media (max-width: 900px){ .how-grid { grid-template-columns: 1fr !important; gap: 32px !important; } .how-grid > div:first-child { position: static !important; } }`}</style>
      </section>

      {/* VALUES */}
      <section className="section-pad" style={{ background: 'var(--ink)', color: 'var(--paper)' }}>
        <div className="container-zt">
          <div style={{ maxWidth: 760 }}>
            <div className="eyebrow" style={{ color: 'var(--copper)' }}>{t('values.eyebrow')}</div>
            <h2 className="font-display h-section" style={{ marginTop: 12, color: 'var(--paper)' }}>{t('values.heading')}</h2>
            <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 16, lineHeight: 1.7, opacity: 0.88 }}>
              <p>{t('values.p1')}</p>
              <p>{t('values.p2')}</p>
              <p>{t('values.p3')}</p>
            </div>
          </div>
          <div style={{ marginTop: 56, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }} className="values-grid">
            {[
              { icon: CheckCircle2, k: 'v1' },
              { icon: Eye, k: 'v2' },
              { icon: Scale, k: 'v3' },
              { icon: ShieldCheck, k: 'v4' },
            ].map(({ icon: Icon, k }) => (
              <div key={k} style={{ padding: 24, border: '1px solid rgba(244,239,230,0.16)', borderRadius: 8 }}>
                <Icon size={22} strokeWidth={1.5} />
                <div className="font-display" style={{ marginTop: 16, fontSize: 20 }}>{t(`values.${k}`)}</div>
              </div>
            ))}
          </div>
        </div>
        <style>{`@media (max-width: 900px){ .values-grid { grid-template-columns: repeat(2, 1fr) !important; } }`}</style>
      </section>

      {/* CITIES */}
      <section className="section-pad">
        <div className="container-zt">
          <div style={{ maxWidth: 720 }}>
            <div className="eyebrow">{t('cities.eyebrow')}</div>
            <h2 className="font-display h-section" style={{ marginTop: 12 }}>{t('cities.heading')}</h2>
            <p style={{ marginTop: 16, fontSize: 17, color: 'var(--ink-2)', lineHeight: 1.6 }}>{t('cities.subheading')}</p>
          </div>
          <div style={{ marginTop: 40, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {[1,2,3,4,5,6,7,8].map((i) => (
              <span key={i} className="city-tag"><span className="dot" />{t(`cities.c${i}`)}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="section-pad" style={{ background: 'var(--paper-2)' }}>
        <div className="container-zt">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64 }} className="contact-grid">
            <div>
              <div className="eyebrow">{t('contact.eyebrow')}</div>
              <h2 className="font-display h-section" style={{ marginTop: 12 }}>{t('contact.heading')}</h2>
              <p style={{ marginTop: 16, fontSize: 17, color: 'var(--ink-2)', lineHeight: 1.6 }}>{t('contact.subheading')}</p>
              <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 12, fontSize: 15 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}><MapPin size={18} style={{ marginTop: 2 }} /><span>{t('contact.address')}</span></div>
                <div><span style={{ color: 'var(--stone)', fontSize: 14 }}>{t('contact.orEmail')} </span><a href="mailto:hola@zantravina.com" className="link-inline">hola@zantravina.com</a></div>
                <div><a href={`tel:${t('contact.phone').replace(/\s+/g,'')}`} className="link-inline">{t('contact.phone')}</a></div>
              </div>
            </div>
            <div style={{ background: 'var(--paper)', padding: 32, borderRadius: 12, border: '1px solid var(--line)' }}>
              <ContactForm />
            </div>
          </div>
        </div>
        <style>{`@media (max-width: 900px){ .contact-grid { grid-template-columns: 1fr !important; gap: 32px !important; } }`}</style>
      </section>
    </div>
  );
}
